import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { User } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import type { Project, UserProfile } from '@/lib/types'
import { cn, LIST_COLORS, labelColor, projectDotClass } from '@/lib/utils'
import { htmlToText } from '@/lib/richtext'
import type { TimeFilter } from '@/lib/timeFilter'
import { createPortal } from 'react-dom'
import { MemberList } from './MemberList'
import { TimeFilterMenu } from './TimeFilterMenu'
import {
  Search,
  Loader2,
  ChevronDown,
  UserCog,
  LogOut,
  Tag,
  MessageCircle,
  Archive,
  Users,
  X,
  Star,
  Rows3,
  Check,
  HelpCircle,
  CalendarDays,
  Flame,
  MessagesSquare,
  Columns3,
  StretchHorizontal,
  Trash2,
} from 'lucide-react'

interface Props {
  user: User
  project: Project | undefined
  archivedCount: number
  /** Archived cards of the current project matching the active search. */
  matchedArchivedCount: number
  /** Total people who can be subscribed on cards of the current project
   *  (owner + project_members). Drives a small badge next to « Membres ». */
  memberCount: number
  /** Full list of member user_ids on the current project (owner first,
   *  then collaborators). Used to render the hover popover that lists
   *  every member with their avatar and name — same component as the
   *  card-level AvatarStack popover, anchored to the « Membres » button. */
  memberIds: string[]
  /** Lookup table for displaying member avatars + names. */
  profilesById: Record<string, UserProfile>
  onOpenArchives: () => void
  onOpenMembers: () => void
  onOpenProfile: () => void
  onSignOut: () => void
  /** Open a card (and switch to its project) from a search result. */
  onOpenTicket: (ticketId: string, projectId: string) => void
  /** Live search query — drives the board filter (highlight matches, hide the rest). */
  onSearchChange: (query: string) => void
  /** Rename the currently-selected project (same handler as the sidebar). */
  onRenameProject: (id: string, name: string) => void | Promise<void>
  /** Update the current project's accent color (null clears it). Drives the
   *  swatch button left of the project name AND the sidebar tile color. */
  onSetProjectColor: (id: string, color: string | null) => void | Promise<void>
  /** Whether the board is currently filtered to favorite cards only. */
  favoritesOnly: boolean
  /** Total favorited cards in the current project — drives the count badge
   *  next to the « Favoris » button. */
  favoriteCount: number
  /** Toggle the "favorites only" filter on the board. */
  onToggleFavoritesOnly: () => void
  /** Whether the board is filtered to conversations awaiting our reply. */
  awaitingOnly: boolean
  /** Count of cards awaiting a reply in the current project — count badge. */
  awaitingCount: number
  /** Toggle the "awaiting reply only" filter on the board. */
  onToggleAwaitingOnly: () => void
  /** AI-derived filter toggles. Independent — combinable via AND so the
   *  user can ask for "only questions WITH a date", etc. The three keys
   *  mirror the per-card icons (urgent / question / date). */
  aiFilters: { question: boolean; date: boolean; urgent: boolean }
  /** Per-project counts of cards matching each AI signal — drives the
   *  small badges next to each filter button. */
  aiFilterCounts: { question: number; date: number; urgent: number }
  /** Flip a single AI filter on / off. */
  onToggleAiFilter: (key: 'question' | 'date' | 'urgent') => void
  /** Distinct labels in use anywhere on the currently-selected project.
   *  Drives the « Étiquettes » multi-select dropdown. */
  availableLabels: string[]
  /** Label → count of ACTIVE (non-archived) cards carrying it. A label at
   *  0 is orphaned and gets a delete affordance in the dropdown. */
  labelCardCounts: Record<string, number>
  /** Permanently delete a label from the project (confirmation handled by
   *  the parent). Only offered for orphaned labels. */
  onDeleteLabel: (label: string) => void
  /** Labels the user has piled up — board is restricted to cards that
   *  carry EVERY one of them (AND). */
  selectedLabels: Set<string>
  /** Toggle a single label in / out of the active selection. */
  onToggleLabel: (label: string) => void
  /** Drop all selected labels at once. */
  onClearLabels: () => void
  /** Active temporal filter (« depuis combien de temps la carte est là »). */
  timeFilter: TimeFilter
  /** Replace the temporal filter. */
  onTimeFilterChange: (f: TimeFilter) => void
  /** Current board layout — Kanban columns or the mail-style list stack. */
  boardView: 'kanban' | 'list'
  /** Switch the board layout (persisted per-user). */
  onBoardViewChange: (v: 'kanban' | 'list') => void
  /** When true, every card on the board renders in a slim one-line layout. */
  compactCards: boolean
  /** Toggle the compact card layout (persisted per-user). */
  onToggleCompactCards: () => void
  /** When true (default), action buttons (Compact, Archives, Membres,
   *  Importer) and the icon filters (Urgent, Question, Agenda, Favoris)
   *  render with their text label. When false, only icons + count badges
   *  remain — denser top bar for users with crowded toolbars. Driven by
   *  the `toolbar_labels` user preference. */
  toolbarLabels: boolean
}

interface SearchResult {
  kind: 'ticket' | 'message'
  ticketId: string
  projectId: string
  title: string
  snippet: string
  archived: boolean
}

function initials(nameOrEmail: string): string {
  const base = nameOrEmail.split('@')[0].replace(/[^\p{L}\p{N}\s.]/gu, ' ').trim()
  const parts = base.split(/[\s.]+/).filter(Boolean).slice(0, 2)
  return parts.map((p) => p[0]?.toUpperCase()).join('') || '?'
}

export function TopBar({
  user,
  project,
  archivedCount,
  matchedArchivedCount,
  memberCount,
  memberIds,
  profilesById,
  onOpenArchives,
  onOpenMembers,
  onOpenProfile,
  onSignOut,
  onOpenTicket,
  onSearchChange,
  onRenameProject,
  onSetProjectColor,
  favoritesOnly,
  favoriteCount,
  onToggleFavoritesOnly,
  awaitingOnly,
  awaitingCount,
  onToggleAwaitingOnly,
  aiFilters,
  aiFilterCounts,
  onToggleAiFilter,
  availableLabels,
  labelCardCounts,
  onDeleteLabel,
  selectedLabels,
  onToggleLabel,
  onClearLabels,
  timeFilter,
  onTimeFilterChange,
  boardView,
  onBoardViewChange,
  compactCards,
  onToggleCompactCards,
  toolbarLabels,
}: Props) {
  const { t } = useTranslation()
  const meta = user.user_metadata ?? {}
  const displayName: string = meta.display_name || user.email || ''
  const avatarUrl: string | null = meta.avatar_url ?? null

  const [menuOpen, setMenuOpen] = useState(false)
  const [q, setQ] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [searching, setSearching] = useState(false)
  const [resultsOpen, setResultsOpen] = useState(false)
  const searchRef = useRef<HTMLDivElement | null>(null)

  // « Étiquettes » multi-select dropdown — chip cumulator. Outside-click
  // dismisses it, same handler shape as the user menu below.
  const [labelMenuOpen, setLabelMenuOpen] = useState(false)
  const [labelFilter, setLabelFilter] = useState('')
  const labelMenuRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!labelMenuOpen) return
    function onDocDown(e: MouseEvent) {
      if (!labelMenuRef.current?.contains(e.target as Node)) {
        setLabelMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocDown)
    return () => document.removeEventListener('mousedown', onDocDown)
  }, [labelMenuOpen])
  // Clear the local filter input every time the menu closes so the next
  // open starts on the full list, not whatever was typed last time.
  useEffect(() => {
    if (!labelMenuOpen) setLabelFilter('')
  }, [labelMenuOpen])
  const labelsToShow = availableLabels.filter((l) =>
    labelFilter.trim()
      ? l.toLowerCase().includes(labelFilter.trim().toLowerCase())
      : true,
  )

  // « Membres » button: native click still opens the full ProjectMembers
  // modal, but on hover we also surface a small popover listing every
  // collaborator (avatar + name + « Vous » badge on the current viewer).
  // Same MemberList component the card-level AvatarStack uses.
  const [memHoverOpen, setMemHoverOpen] = useState(false)
  const [memPos, setMemPos] = useState<{ top: number; left: number } | null>(
    null,
  )
  const memBtnRef = useRef<HTMLButtonElement | null>(null)
  const memPopRef = useRef<HTMLDivElement | null>(null)
  const memCloseTimer = useRef<number | null>(null)
  function openMemHover() {
    if (memCloseTimer.current) {
      window.clearTimeout(memCloseTimer.current)
      memCloseTimer.current = null
    }
    const r = memBtnRef.current?.getBoundingClientRect()
    if (!r) return
    setMemPos({ top: r.bottom + 6, left: r.right })
    setMemHoverOpen(true)
  }
  function closeMemHoverSoon() {
    if (memCloseTimer.current) window.clearTimeout(memCloseTimer.current)
    memCloseTimer.current = window.setTimeout(() => {
      setMemHoverOpen(false)
      memCloseTimer.current = null
    }, 120)
  }
  useEffect(() => {
    if (!memHoverOpen) return
    const pop = memPopRef.current
    const btn = memBtnRef.current
    if (!pop || !btn) return
    const popR = pop.getBoundingClientRect()
    const btnR = btn.getBoundingClientRect()
    const PAD = 8
    let left = btnR.right - popR.width
    if (left < PAD) left = PAD
    if (left + popR.width > window.innerWidth - PAD)
      left = window.innerWidth - PAD - popR.width
    let top = btnR.bottom + 6
    if (top + popR.height > window.innerHeight - PAD) {
      const flipped = btnR.top - popR.height - 6
      if (flipped >= PAD) top = flipped
    }
    setMemPos({ top, left })
  }, [memHoverOpen])

  // Color-picker popover anchored to the project swatch (left of the name).
  const [colorOpen, setColorOpen] = useState(false)
  const colorRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!colorOpen) return
    function onClick(e: MouseEvent) {
      if (colorRef.current && !colorRef.current.contains(e.target as Node))
        setColorOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setColorOpen(false)
    }
    window.addEventListener('mousedown', onClick)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onClick)
      window.removeEventListener('keydown', onKey)
    }
  }, [colorOpen])

  // Project title rename: double-click the title → swap to an input, Enter
  // or blur commits, Escape cancels. We read the value directly from the DOM
  // element at commit time (not from React state) to avoid race conditions
  // when blur and onChange fire close together — controlled-input updates
  // could otherwise lag behind the user's last keystroke.
  const [renaming, setRenaming] = useState(false)
  const [renameDraft, setRenameDraft] = useState('')
  const renameRef = useRef<HTMLInputElement | null>(null)
  useEffect(() => {
    if (renaming && renameRef.current) {
      renameRef.current.focus()
      renameRef.current.select()
    }
  }, [renaming])
  // Cancellation flag: when Escape is pressed we set this so the subsequent
  // onBlur (fired as the input unmounts and loses focus) does NOT commit.
  const cancelBlurRef = useRef(false)
  function startRename() {
    if (!project) return
    setRenameDraft(project.name)
    setRenaming(true)
  }
  function commitFromInput(input: HTMLInputElement) {
    if (!project) return
    const next = input.value.trim()
    if (next && next !== project.name) onRenameProject(project.id, next)
    setRenaming(false)
  }

  // Close menus on outside click
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setResultsOpen(false)
      }
      setMenuOpen(false)
    }
    window.addEventListener('click', onDown)
    return () => window.removeEventListener('click', onDown)
  }, [])

  // Debounced search across tickets (title/description) + messages (body)
  useEffect(() => {
    const term = q.trim().replace(/[,()%_]/g, ' ').trim()
    if (term.length < 2) {
      setResults([])
      setSearching(false)
      return
    }
    let cancelled = false
    setSearching(true)
    const timer = setTimeout(async () => {
      try {
        const [{ data: tk }, { data: msgs }] = await Promise.all([
          supabase
            .from('tickets')
            .select('id, project_id, title, description, archived')
            .or(`title.ilike.%${term}%,description.ilike.%${term}%`)
            .limit(8),
          supabase
            .from('messages')
            .select('id, project_id, body')
            .ilike('body', `%${term}%`)
            .limit(8),
        ])

        const out: SearchResult[] = (tk ?? []).map((t) => ({
          kind: 'ticket' as const,
          ticketId: t.id,
          projectId: t.project_id,
          title: t.title,
          snippet: htmlToText(t.description ?? '').slice(0, 90),
          archived: !!t.archived,
        }))

        // Resolve matched messages to the ticket that references them.
        const msgIds = (msgs ?? []).map((m) => m.id)
        if (msgIds.length) {
          const { data: owning } = await supabase
            .from('tickets')
            .select('id, project_id, title, source_message_ids, archived')
            .overlaps('source_message_ids', msgIds)
          const seen = new Set(out.map((r) => r.ticketId))
          for (const m of msgs ?? []) {
            const owner = (owning ?? []).find((t) =>
              (t.source_message_ids ?? []).includes(m.id),
            )
            if (owner && !seen.has(owner.id)) {
              seen.add(owner.id)
              out.push({
                kind: 'message',
                ticketId: owner.id,
                projectId: owner.project_id,
                title: owner.title,
                snippet: m.body?.slice(0, 90) ?? '',
                archived: !!owner.archived,
              })
            }
          }
        }

        if (cancelled) return
        setResults(out.slice(0, 12))
        setSearching(false)
      } catch (e) {
        if (!cancelled) {
          console.error('search failed', e)
          setSearching(false)
        }
      }
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [q])

  function pick(r: SearchResult) {
    setResultsOpen(false)
    setQ('')
    setResults([])
    onOpenTicket(r.ticketId, r.projectId)
  }

  return (
    <header className="flex shrink-0 items-center gap-4 border-b border-[var(--color-border-soft)] bg-[var(--color-surface)] px-5 py-2.5">
      {/* Project identity — color dot + double-click to rename. */}
      <div className="flex min-w-0 shrink-0 items-center gap-2">
        {/* Accent swatch — click to open the colour picker. Hidden while
            renaming so the input gets full focus. */}
        {project && !renaming && (
          <div ref={colorRef} className="relative">
            <button
              type="button"
              onClick={() => setColorOpen((v) => !v)}
              aria-label={t('topbar.chooseProjectColor')}
              title={t('topbar.projectColor')}
              className={cn(
                'h-3.5 w-3.5 shrink-0 rounded-full ring-2 ring-offset-1 ring-offset-[var(--color-surface)] transition-transform hover:scale-110',
                projectDotClass(project.color, project.id),
                colorOpen ? 'ring-[var(--color-text)]' : 'ring-transparent',
              )}
            />
            {colorOpen && (
              // 12 pastels arranged as a 6×2 flex-wrap, mirroring the
              // bubble palette used in the sidebar tiles so the picker
              // previews exactly what the project tile will look like
              // once applied. The selected swatch shows a small check
              // icon in the matching dark family colour (inherited via
              // `text-*-700` from the bubble class) — keeps the rounded
              // edge intact and avoids the white halo "rubbing" against
              // neighbouring swatches.
              //
              // The popover is absolutely positioned with no explicit
              // width on its containing block, so it uses shrink-to-fit
              // sizing. A CSS grid with `grid-cols-6` (= repeat(6,
              // minmax(0, 1fr))) lets each column collapse to 0, which
              // shrink-to-fit happily evaluates as a 0-width valid
              // layout, mashing every swatch into the same point. Using
              // `flex flex-wrap` with an explicit popover width gives
              // each `w-6 shrink-0` button its full intrinsic 24px and
              // forces exactly 6 per row.
              <div className="absolute left-0 top-[calc(100%+6px)] z-50 flex w-[196px] flex-wrap gap-1.5 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-2 shadow-[var(--shadow-modal)]">
                {LIST_COLORS.map((c) => {
                  const selected = project.color === c.key
                  return (
                    <button
                      key={c.key}
                      type="button"
                      title={c.label}
                      aria-pressed={selected}
                      onClick={() => {
                        onSetProjectColor(
                          project.id,
                          selected ? null : c.key,
                        )
                        setColorOpen(false)
                      }}
                      className={cn(
                        'relative inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-transform hover:z-10 hover:scale-110',
                        c.bubble,
                      )}
                    >
                      {selected && (
                        <Check className="h-3.5 w-3.5" strokeWidth={3} />
                      )}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )}
        {renaming && project ? (
          <input
            ref={renameRef}
            value={renameDraft}
            onChange={(e) => setRenameDraft(e.target.value)}
            onBlur={(e) => {
              if (cancelBlurRef.current) {
                cancelBlurRef.current = false
                setRenaming(false)
                return
              }
              commitFromInput(e.currentTarget)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                commitFromInput(e.currentTarget)
              }
              if (e.key === 'Escape') {
                cancelBlurRef.current = true
                setRenaming(false)
              }
            }}
            className="w-[260px] rounded-md border border-[var(--color-link)] bg-[var(--color-surface)] px-1.5 py-0.5 text-[15px] font-semibold text-[var(--color-text-strong)] outline-none"
          />
        ) : (
          <h1
            className="truncate text-[15px] font-semibold text-[var(--color-text-strong)] cursor-text select-none"
            title={project ? t('topbar.doubleClickToRename') : undefined}
            onDoubleClick={() => project && startRename()}
          >
            {project?.name ?? t('topbar.noProject')}
          </h1>
        )}
      </div>

      {/* Unified search + filter bar — the live text query and the two
          board-narrowing filters (labels + time) live inside ONE pill so
          every criterion combines in the same place (AND), following the
          « search · filters » pattern used by Linear/Notion. */}
      <div ref={searchRef} className="relative mx-auto w-full max-w-2xl flex-1">
        <div className="flex items-center rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] transition-colors focus-within:border-[var(--color-link)] focus-within:bg-white">
          <Search className="pointer-events-none ml-3.5 h-4.5 w-4.5 shrink-0 text-[var(--color-text-dim)]" />
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value)
              setResultsOpen(true)
              onSearchChange(e.target.value)
            }}
            onFocus={() => setResultsOpen(true)}
            placeholder={t('topbar.searchPlaceholder')}
            // Kill the browser's native inline autocomplete: without these
            // it inlay-completes the field from history and paints the
            // suggested suffix as a blue highlighted box over the input
            // (now visible since the field background went transparent).
            // We already render our own results list below, so the
            // browser's predictive overlay is pure noise.
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            className="min-w-0 flex-1 bg-transparent py-2.5 pl-2.5 pr-2 text-[13.5px] text-[var(--color-text)] outline-none [&::-webkit-search-cancel-button]:hidden"
          />
          {searching ? (
            <Loader2 className="mr-1 h-3.5 w-3.5 shrink-0 animate-spin text-[var(--color-text-dim)]" />
          ) : q ? (
            <button
              type="button"
              onClick={() => {
                setQ('')
                setResults([])
                setResultsOpen(false)
                onSearchChange('')
              }}
              aria-label={t('topbar.clearSearch')}
              className="mr-0.5 shrink-0 rounded-md p-1 text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}

          {/* Divider, then the in-bar filters. Each filter is a fully
              standalone, recessed « chip » button — a clear border + a
              surface tint make them read as controls inlaid INTO the
              search field, distinct from each other and from the text
              query. They sit in a gap-1 row so the two never touch. */}
          <div className="mx-1 h-5 w-px shrink-0 bg-[var(--color-border-soft)]" />
          <div className="flex shrink-0 items-center gap-1 pr-1.5">
          {/* Labels multi-select — the popover lists one chip per existing
              label, several can be stacked, and the board narrows to cards
              carrying EVERY selected one (AND). */}
          {availableLabels.length > 0 && (
            <div ref={labelMenuRef} className="relative shrink-0">
              <button
                type="button"
                onClick={() => {
                  setResultsOpen(false)
                  setLabelMenuOpen((v) => !v)
                }}
                aria-pressed={selectedLabels.size > 0}
                title={t('topbar.filterLabelsTitle')}
                className={cn(
                  'inline-flex items-center gap-1 rounded-lg border px-2 py-1.5 transition-colors',
                  selectedLabels.size > 0
                    ? 'border-violet-200 bg-violet-100 text-violet-700'
                    : 'border-[var(--color-border-soft)] bg-[var(--color-surface-2)] text-[var(--color-text-dim)] hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]',
                )}
              >
                <Tag className="h-4 w-4" />
                {selectedLabels.size > 0 && (
                  <span className="rounded-full bg-violet-200 px-1 text-[10px] font-semibold tabular-nums text-violet-900">
                    {selectedLabels.size}
                  </span>
                )}
              </button>
              {labelMenuOpen && (
                <div className="absolute right-0 top-[calc(100%+6px)] z-40 w-72 overflow-hidden rounded-xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] shadow-[var(--shadow-modal)]">
                  <div className="border-b border-[var(--color-border-soft)] px-3 py-2.5">
                    <div className="mb-1.5 flex items-center justify-between gap-2">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-dim)]">
                        {t('topbar.filterLabels')}
                      </span>
                      {selectedLabels.size > 0 && (
                        <button
                          type="button"
                          onClick={onClearLabels}
                          className="text-[10.5px] font-medium text-[var(--color-text-dim)] transition-colors hover:text-[var(--color-text)]"
                        >
                          {t('topbar.filterLabelsClear')}
                        </button>
                      )}
                    </div>
                    <p className="mb-2 text-[11px] leading-relaxed text-[var(--color-text-dim)]">
                      {t('topbar.filterLabelsHelp')}
                    </p>
                    {availableLabels.length > 8 && (
                      <input
                        type="text"
                        autoFocus
                        value={labelFilter}
                        onChange={(e) => setLabelFilter(e.target.value)}
                        placeholder={t('topbar.filterLabelsSearch')}
                        className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-[12px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-link)]"
                      />
                    )}
                  </div>
                  <div className="max-h-72 overflow-y-auto px-3 py-2">
                    {labelsToShow.length === 0 ? (
                      <p className="px-1 py-2 text-[11.5px] text-[var(--color-text-dim)]">
                        {t('topbar.filterLabelsNoMatch')}
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {labelsToShow.map((l) => {
                          const active = selectedLabels.has(l)
                          // Orphan = no active (non-archived) card carries it.
                          // Those get a trash affordance to prune the label.
                          const orphan = (labelCardCounts[l] ?? 0) === 0
                          return (
                            <span
                              key={l}
                              className={cn(
                                // Same deterministic colour the label gets on
                                // the cards, so the filter reads as « the SMD
                                // chip » etc. Selected = thicker ring; idle
                                // chips dim slightly so the active ones pop.
                                'inline-flex items-center overflow-hidden rounded-full text-[11.5px] font-medium ring-inset',
                                labelColor(l),
                                active ? 'ring-2' : 'ring-1 opacity-70',
                              )}
                            >
                              <button
                                type="button"
                                onClick={() => onToggleLabel(l)}
                                className="inline-flex items-center gap-1 py-1 pl-2 pr-1.5 transition-colors hover:bg-black/5"
                              >
                                {active && (
                                  <Check className="h-3 w-3" strokeWidth={3} />
                                )}
                                <span className="max-w-[140px] truncate">{l}</span>
                                {orphan && (
                                  <span
                                    title={t('topbar.filterLabelsOrphan')}
                                    className="rounded-full bg-[var(--color-surface-3)] px-1 text-[9px] font-semibold tabular-nums text-[var(--color-text-dim)]"
                                  >
                                    0
                                  </span>
                                )}
                              </button>
                              {orphan && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    onDeleteLabel(l)
                                  }}
                                  title={t('topbar.filterLabelsDelete', { label: l })}
                                  aria-label={t('topbar.filterLabelsDelete', { label: l })}
                                  className="inline-flex items-center self-stretch border-l border-[var(--color-border-soft)] px-1.5 text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-danger-bg)] hover:text-[var(--color-danger)]"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              )}
                            </span>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Temporal filter — how long a card has been sitting on the
              board. Same recessed-chip treatment as the label button. */}
          <TimeFilterMenu
            value={timeFilter}
            onChange={onTimeFilterChange}
            toolbarLabels={toolbarLabels}
            compact
            onOpen={() => setResultsOpen(false)}
          />
          </div>
        </div>

        {resultsOpen && q.trim().length >= 2 && (
          <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-40 max-h-[60vh] overflow-y-auto rounded-2xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] py-1.5 shadow-[var(--shadow-modal)]">
            {!searching && results.length === 0 && (
              <div className="px-4 py-3 text-[12px] text-[var(--color-text-dim)]">
                {t('topbar.noResults')}
              </div>
            )}
            {results.map((r) => (
              <button
                key={`${r.kind}-${r.ticketId}`}
                type="button"
                onClick={() => pick(r)}
                className="flex w-full items-start gap-2.5 px-4 py-2 text-left transition-colors hover:bg-[var(--color-surface-2)]"
              >
                <span className="mt-0.5 text-[var(--color-text-dim)]">
                  {r.kind === 'ticket' ? (
                    <Tag className="h-3.5 w-3.5" />
                  ) : (
                    <MessageCircle className="h-3.5 w-3.5" />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span className="truncate text-[12.5px] font-medium text-[var(--color-text-strong)]">
                      {r.title}
                    </span>
                    {r.archived && (
                      <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-[var(--color-surface-3)] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-[var(--color-text-dim)]">
                        <Archive className="h-2.5 w-2.5" />
                        {t('topbar.archived')}
                      </span>
                    )}
                  </span>
                  {r.snippet && (
                    <span className="block truncate text-[11px] text-[var(--color-text-dim)]">
                      {r.kind === 'message' ? t('topbar.messagePrefix') : ''}
                      {r.snippet}
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* View switcher — Kanban columns vs the mail-style list stack. */}
      <div className="flex h-9 shrink-0 items-center gap-0.5 rounded-lg border border-[var(--color-border-soft)] bg-[var(--color-surface)] px-1">
        <button
          type="button"
          onClick={() => onBoardViewChange('kanban')}
          aria-pressed={boardView === 'kanban'}
          title={t('topbar.viewKanban')}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] font-medium transition-colors',
            boardView === 'kanban'
              ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
              : 'text-[var(--color-text-dim)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]',
          )}
        >
          <Columns3 className="h-3.5 w-3.5" />
          {toolbarLabels && t('topbar.viewKanban')}
        </button>
        <button
          type="button"
          onClick={() => onBoardViewChange('list')}
          aria-pressed={boardView === 'list'}
          title={t('topbar.viewList')}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] font-medium transition-colors',
            boardView === 'list'
              ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
              : 'text-[var(--color-text-dim)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]',
          )}
        >
          <StretchHorizontal className="h-3.5 w-3.5" />
          {toolbarLabels && t('topbar.viewList')}
        </button>
      </div>

      {/* Compact card layout — its own frame so it never disappears (and
          never reshuffles the toolbar) when switching views. It only acts
          on the Kanban board, so it's disabled — but kept visible — in
          list mode rather than vanishing. */}
      <div className="flex h-9 shrink-0 items-center rounded-lg border border-[var(--color-border-soft)] bg-[var(--color-surface)] px-1">
        <button
          type="button"
          onClick={onToggleCompactCards}
          disabled={boardView === 'list'}
          aria-pressed={compactCards}
          title={
            boardView === 'list'
              ? t('topbar.compactKanbanOnly')
              : compactCards
                ? t('topbar.compactSwitchOff')
                : t('topbar.compactSwitchOn')
          }
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] font-medium transition-colors',
            boardView === 'list'
              ? 'cursor-not-allowed text-[var(--color-text-dim)]/40'
              : compactCards
                ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
                : 'text-[var(--color-text-dim)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]',
          )}
        >
          <Rows3 className="h-3.5 w-3.5" />
          {toolbarLabels && t('topbar.compact')}
        </button>
      </div>

      {/* AI filter chips — four independent toggles (urgent / question /
          agenda / favorites) matching the per-card icons. Combinable via
          AND: turning on both "question" and "agenda" shows only cards
          that have both signals. Each shows a tiny count badge of matching
          cards in the current project. The "0" state is dimmed but still
          clickable (so the user can pre-set a filter before importing).
          When toolbarLabels is true (user pref), each chip also shows its
          text label between the icon and the count. */}
      <div className="flex h-9 shrink-0 items-center gap-1 rounded-lg border border-[var(--color-border-soft)] bg-[var(--color-surface)] px-1">
        <AiFilterButton
          icon={<Flame className="h-3.5 w-3.5" strokeWidth={2.5} />}
          label={t('topbar.filterUrgent')}
          showLabel={toolbarLabels}
          active={aiFilters.urgent}
          count={aiFilterCounts.urgent}
          activeColor="text-rose-700 bg-rose-100"
          onClick={() => onToggleAiFilter('urgent')}
        />
        <AiFilterButton
          icon={<HelpCircle className="h-3.5 w-3.5" strokeWidth={2.5} />}
          label={t('topbar.filterQuestion')}
          showLabel={toolbarLabels}
          active={aiFilters.question}
          count={aiFilterCounts.question}
          activeColor="text-amber-700 bg-amber-100"
          onClick={() => onToggleAiFilter('question')}
        />
        <AiFilterButton
          icon={<CalendarDays className="h-3.5 w-3.5" strokeWidth={2.5} />}
          label={t('topbar.filterAgenda')}
          showLabel={toolbarLabels}
          active={aiFilters.date}
          count={aiFilterCounts.date}
          activeColor="text-sky-700 bg-sky-100"
          onClick={() => onToggleAiFilter('date')}
        />
        {/* Favorites filter — was a standalone button, now part of the
            icon-filter group for visual consistency with the other 3
            AI filters. Star is filled when active. */}
        <AiFilterButton
          icon={
            <Star
              className="h-3.5 w-3.5"
              fill={favoritesOnly ? 'currentColor' : 'none'}
              strokeWidth={favoritesOnly ? 1.5 : 2.5}
            />
          }
          label={t('topbar.favorites')}
          showLabel={toolbarLabels}
          active={favoritesOnly}
          count={favoriteCount}
          activeColor="text-amber-700 bg-amber-100"
          onClick={onToggleFavoritesOnly}
        />
        {/* Awaiting-reply filter — conversations where the contact spoke
            last and we still owe a reply. Mirrors the card's green
            messaging bubble (filled when active). */}
        <AiFilterButton
          icon={
            <MessagesSquare
              className="h-3.5 w-3.5"
              fill={awaitingOnly ? 'currentColor' : 'none'}
              strokeWidth={awaitingOnly ? 1.5 : 2.5}
            />
          }
          label={t('topbar.filterAwaiting')}
          showLabel={toolbarLabels}
          active={awaitingOnly}
          count={awaitingCount}
          activeColor="text-emerald-700 bg-emerald-100"
          onClick={onToggleAwaitingOnly}
        />
      </div>

      {/* Actions cluster — archives / members / import grouped into one
          bordered pill so the three secondary controls read as a single
          tidy unit instead of three floating icons. */}
      <div className="flex h-9 shrink-0 items-center gap-0.5 rounded-lg border border-[var(--color-border-soft)] bg-[var(--color-surface)] px-1">
        {/* Archives — lights up when the active search has matches inside */}
        <button
          type="button"
          onClick={onOpenArchives}
          title={
            matchedArchivedCount > 0
              ? t('topbar.archivesMatchTitle', { count: matchedArchivedCount })
              : archivedCount > 0
                ? t('topbar.archivesCountTitle', { count: archivedCount })
                : t('topbar.archivesNoneTitle')
          }
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] font-medium transition-colors',
            matchedArchivedCount > 0
              ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] ring-1 ring-inset ring-[var(--color-accent)]/40 hover:bg-[var(--color-accent-bg)]/70'
              : 'text-[var(--color-text-dim)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]',
          )}
        >
          <Archive className="h-3.5 w-3.5" />
          {toolbarLabels && t('topbar.archives')}
          {matchedArchivedCount > 0 ? (
            <span className="inline-flex min-w-[18px] items-center justify-center rounded-full bg-[var(--color-accent)] px-1 py-0.5 text-[10px] font-bold text-white">
              {matchedArchivedCount}
            </span>
          ) : archivedCount > 0 ? (
            <span className="inline-flex min-w-[18px] items-center justify-center rounded-full bg-[var(--color-surface-3)] px-1 py-0.5 text-[10px] font-semibold text-[var(--color-text-dim)]">
              {archivedCount}
            </span>
          ) : null}
        </button>

        {/* Members (only with a project open). Click → full modal. Hover →
            quick popover listing every collaborator. */}
        {project && (
          <button
            ref={memBtnRef}
            type="button"
            onClick={onOpenMembers}
            onMouseEnter={openMemHover}
            onMouseLeave={closeMemHoverSoon}
            className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] font-medium text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
          >
            <Users className="h-3.5 w-3.5" />
            {toolbarLabels && t('topbar.members')}
            {memberCount > 0 && (
              <span className="inline-flex min-w-[18px] items-center justify-center rounded-full bg-[var(--color-surface-3)] px-1 py-0.5 text-[10px] font-semibold tabular-nums text-[var(--color-text-dim)]">
                {memberCount}
              </span>
            )}
          </button>
        )}

      </div>
      {memHoverOpen &&
        memPos &&
        memberIds.length > 0 &&
        createPortal(
          <div
            ref={memPopRef}
            role="tooltip"
            onMouseEnter={openMemHover}
            onMouseLeave={closeMemHoverSoon}
            style={{ top: memPos.top, left: memPos.left }}
            className="pointer-events-auto fixed z-[80]"
          >
            <MemberList
              userIds={memberIds}
              profilesById={profilesById}
              currentUserId={user.id}
            />
          </div>,
          document.body,
        )}

      {/* Profile menu */}
      <div className="relative shrink-0">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setMenuOpen((v) => !v)
          }}
          className="flex items-center gap-1.5 rounded-xl p-0.5 pr-1.5 transition-colors hover:bg-[var(--color-surface-2)]"
        >
          <span className="h-8 w-8 overflow-hidden rounded-xl bg-[var(--color-surface-2)]">
            {avatarUrl ? (
              <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="flex h-full w-full items-center justify-center text-[11px] font-semibold text-[var(--color-text-dim)]">
                {initials(displayName)}
              </span>
            )}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-[var(--color-text-dim)]" />
        </button>

        {menuOpen && (
          <div
            className="absolute right-0 top-[calc(100%+6px)] z-40 w-56 overflow-hidden rounded-2xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] py-1.5 shadow-[var(--shadow-modal)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-2">
              <div className="truncate text-[13px] font-medium text-[var(--color-text-strong)]">
                {meta.display_name || '—'}
              </div>
              <div className="truncate text-[11px] text-[var(--color-text-dim)]">
                {user.email}
              </div>
            </div>
            <div className="my-1 border-t border-[var(--color-border-soft)]" />
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false)
                onOpenProfile()
              }}
              className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-[12.5px] text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-2)]"
            >
              <UserCog className="h-3.5 w-3.5 text-[var(--color-text-dim)]" />
              {t('topbar.editProfile')}
            </button>
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false)
                onSignOut()
              }}
              className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-[12.5px] text-[var(--color-danger)] transition-colors hover:bg-[var(--color-danger-bg)]"
            >
              <LogOut className="h-3.5 w-3.5" />
              {t('topbar.signOut')}
            </button>
          </div>
        )}
      </div>
    </header>
  )
}

/** Compact icon-only toggle button used by the AI filter group. Renders
 *  the icon + a tiny count badge when there are matching cards. Active
 *  state lights up with a family-tinted pastel bg (matching the card
 *  icons) so the link between "filter" and "card affordance" is
 *  immediate. */
function AiFilterButton({
  icon,
  label,
  showLabel = false,
  active,
  count,
  activeColor,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  /** When true, render the text label between the icon and the count.
   *  Driven by the `toolbar_labels` user preference at the TopBar level
   *  so every filter chip toggles density together. */
  showLabel?: boolean
  active: boolean
  count: number
  /** Tailwind classes applied when active (text-* + bg-*). */
  activeColor: string
  onClick: () => void
}) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={
        active
          ? t('topbar.filterActiveTitle', { label: label.toLowerCase() })
          : t('topbar.filterInactiveTitle', { label: label.toLowerCase() })
      }
      className={cn(
        'inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11.5px] font-medium transition-colors',
        active
          ? activeColor
          : count === 0
            ? 'text-[var(--color-text-dim)]/40 hover:bg-[var(--color-surface-2)]'
            : 'text-[var(--color-text-dim)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]',
      )}
    >
      {icon}
      {showLabel && <span>{label}</span>}
      {count > 0 && (
        <span className="tabular-nums">{count}</span>
      )}
    </button>
  )
}
